'use client';

import { analiticaInstance as analiticaInstanceConfigs } from '@payments-checkout/shared/plugins/analitica/analitica.configs';
import { analiticaStatusPageEvents } from '@payments-checkout/shared/plugins/analitica/statusPage.events';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { StatusPageDataProvider } from '../../../app/providers/statusPageDataProvider';
import { useStatusPageSharedData } from '../../../hooks/useStatusPageSharedData';
import {
  buildCreditCardFeedback,
  buildCreditCardPaymentInfo,
  resolveCreditCardState,
} from '../../creditCardStatus';
import {
  buildDigitalWalletPaymentInfo,
  resolveDigitalWalletState,
} from '../../digitalWalletStatus';
import { LoadingState } from '../../loading';
import { PaymentInfoDisplay } from '../../paymentInfoDisplay';
import { TrackingStatus } from '../../trackingStatus';
import {
  buildPixOrBoletoFeedback,
  buildPixOrBoletoPaymentInfo,
  resolveStatusPageDisplay,
  trackStatusPageViewed,
} from './statusPage.helper';
import { StatusPageLeftContent } from './statusPageLeftContent';
import { StatusPagePrintStyles } from './statusPagePrintStyles';

interface StatusPageProps {
  sessionId: string;
}

export function StatusPage({ sessionId }: StatusPageProps) {
  const router = useRouter();
  const { formatDate, formatMessage } = useIntl();
  const { sessionData, isLoading, error } = useStatusPageSharedData(sessionId);
  const {
    paymentMethod,
    orderNotPaid,
    isWallet,
    isCard,
    hasDelivery,
    statusLabel,
    statusIcon,
    statusIconColor,
    amount,
    subtitle,
  } = resolveStatusPageDisplay(sessionData, formatMessage, formatDate);
  let paymentInfo, feedback, leftContent, customer, showRefreshTimer;
  let showSplitScreen = false;

  const backToStoreUrl =
    sessionData?.flow?.success_url ?? sessionData?.merchant?.url;

  const handlePrintReceipt = () => {
    analiticaStatusPageEvents.events.statusPage.downloadReceiptClicked();

    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  useEffect(() => {
    analiticaInstanceConfigs.updateMetadata((actualMetadata) => ({
      ...actualMetadata,
      user: {
        id: sessionId,
      },
    }));
  }, [sessionId]);

  useEffect(() => {
    if (error || (!isLoading && !sessionData)) {
      router.push('/error/generic');
    }
  }, [error, isLoading, sessionData, router]);

  useEffect(() => {
    trackStatusPageViewed({
      sessionData,
      isWallet,
      isCard,
      orderNotPaid,
      paymentMethod,
    });
  }, [sessionData, isWallet, isCard, orderNotPaid, paymentMethod]);

  useEffect(() => {
    if (!isLoading && sessionData) {
      window.scrollTo(0, 0);
    }
  }, [isLoading, sessionData]);

  if (isLoading || !sessionData) {
    return <LoadingState fullPage />;
  }

  const isCardOrWallet = isWallet || isCard;

  if (isCardOrWallet) {
    const state = isWallet
      ? resolveDigitalWalletState(sessionData.order_status)
      : resolveCreditCardState(sessionData.order_status);

    paymentInfo = isWallet
      ? buildDigitalWalletPaymentInfo(sessionData, state)
      : buildCreditCardPaymentInfo(sessionData, state);

    feedback = buildCreditCardFeedback({
      sessionData,
      state,
      formatMessage,
      formatDate,
      onPrintReceipt: handlePrintReceipt,
    });

    showRefreshTimer = state === 'processing';
  } else {
    paymentInfo = buildPixOrBoletoPaymentInfo(
      sessionData,
      paymentMethod,
      !orderNotPaid
    );

    feedback = buildPixOrBoletoFeedback({
      sessionData,
      orderNotPaid,
      statusLabel,
      statusIcon,
      statusIconColor,
      amount,
      subtitle,
      formatMessage,
      onPrintReceipt: handlePrintReceipt,
    });

    leftContent = (
      <StatusPageLeftContent
        sessionData={sessionData}
        paymentMethod={paymentMethod}
        orderNotPaid={orderNotPaid}
      />
    );
    customer = sessionData?.customer;
    showSplitScreen = orderNotPaid;
  }

  return (
    <StatusPageDataProvider sessionId={sessionId}>
      <div className="status-page-print">
        <StatusPagePrintStyles />
        <TrackingStatus
          leftContent={leftContent}
          customer={customer}
          showSplitScreen={showSplitScreen}
          showRefreshTimer={showRefreshTimer}
          hasDelivery={hasDelivery}
          deliveryData={sessionData?.delivery}
          backToStoreUrl={backToStoreUrl}
          sessionId={sessionId}
          paymentInfo={
            paymentInfo ? (
              <PaymentInfoDisplay payment={paymentInfo} />
            ) : undefined
          }
          feedback={feedback}
        />
      </div>
    </StatusPageDataProvider>
  );
}
